import React, { useState, useEffect, useCallback } from "react";

import { Column, Field, Control, Label, Select, Input, Button } from "rbx";

import { useApolloClient, useMutation } from "react-apollo";
import gql from "graphql-tag";
import moment from "moment";
import { OrderItem } from "../../../components/Trips";
import { DateInput, LocationSelect } from "../../../components/inputs";

import {
  getFilterState,
  saveFilterState,
  calcTimeZone,
  ourToast as toast
} from "../../../utils";
import { calculateOrderDates } from "../../../components/Orders/utils";
import { useCompanyParameters, useSystemCodes } from "../../../utils/hooks";
import { SINGLE_LOCATION_QUERY } from "../../../graphql/Locations/Locations.graphql";
import { ALL_ORDERS_QUERY as GLOBAL_ALL_ORDERS_QUERY } from "../../../graphql/Orders/Orders.graphql";
import TripList from "./TripList";
import Loading from "../../../components/Loading";
import ResourceScheduler from "./ResourceScheduler";
import "./Trips.scss";

const UPDATE_ORDER_MUTATION = gql`
  mutation UPDATE_ORDER_MUTATION(
    $data: OrderUpdateInput!
    $where: OrderWhereUniqueInput!
  ) {
    updateOrder(data: $data, where: $where) {
      RecId
      OrderNumber
      PickupDate
      PickupLocationRecIdRef
      # PickupTime
      PickupDateTime
      ScheduledDeliveryDate
      # ScheduledDeliveryTime
      EstimatedLoad
      EstimatedTravel
      Trip
      Leg
      BayNumber
      StatusRecIdRef
      TripKey
      ComplexTrip
      PickupDriver {
        RecId
        FirstName
        LastName
      }
      PickupTractor {
        RecId
        VehicleNumber
        Description
      }
      Status {
        RecId
        CodeDescription
      }
      Customer {
        RecId
        CustomerNo
        Name
      }
      BookingCode {
        RecId
        Color
        BookingCode
        Description
      }
      Route {
        RecId
        Contract
        LoadTime
        ReturnToTerminalTime
        TravelTime
        UnloadTime

        StartLocation {
          RecId
          LocationID
          LocationName
          City
          State
        }
        PickupLocation {
          RecId
          LocationID
          LocationName
          City
          State
          TimeZone {
            Type
            Category
            CodeName
            CodeId
            CodeDescription
          }
        }
        EndingLocation {
          RecId
          LocationID
          LocationName
          City
          State
        }
        DeliveryLocation {
          RecId
          LocationID
          LocationName
          City
          State
          TimeZone {
            Type
            Category
            CodeName
            CodeId
            CodeDescription
          }
        }
      }
    }
  }
`;
const ALL_ORDERS_QUERY = gql`
  query ALL_ORDERS_QUERY($where: OrderWhereInput!) {
    Orders(where: $where, orderByMulti: [OrderNumber_ASC]) {
      RecId
      OrderNumber
      PickupDate
      PickupLocationRecIdRef
      # PickupTime
      PickupDateTime
      ScheduledDeliveryDate
      # ScheduledDeliveryTime
      EstimatedLoad
      EstimatedTravel
      Trip
      Leg
      TripKey
      BayNumber
      StatusRecIdRef
      ComplexTrip
      PickupLocation {
        RecId
        LocationID
        LocationName
        City
        State
        TimeZone {
          Type
          Category
          CodeName
          CodeId
          CodeDescription
        }
      }
      PickupDriver {
        RecId
        FirstName
        LastName
      }
      PickupTractor {
        RecId
        VehicleNumber
        Description
      }
      Status {
        RecId
        CodeDescription
      }
      Customer {
        RecId
        CustomerNo
        Name
      }
      BookingCode {
        RecId
        Color
        BookingCode
        Description
      }
      Route {
        RecId
        Contract
        LoadTime
        ReturnToTerminalTime
        TravelTime
        UnloadTime

        StartLocation {
          RecId
          LocationID
          LocationName
          City
          State
        }
        PickupLocation {
          RecId
          LocationID
          LocationName
          City
          State
          TimeZone {
            Type
            Category
            CodeName
            CodeId
            CodeDescription
          }
        }
        EndingLocation {
          RecId
          LocationID
          LocationName
          City
          State
        }
        DeliveryLocation {
          RecId
          LocationID
          LocationName
          City
          State
          TimeZone {
            Type
            Category
            CodeName
            CodeId
            CodeDescription
          }
        }
      }
    }
  }
`;

const useFetch = (PickupDate, PickupLocationRecIdRef) => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [scales, setScales] = useState(2);
  const [pickupLocations, setPickupLocations] = useState([]);

  const client = useApolloClient();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: orderData } = await client.query({
          query: ALL_ORDERS_QUERY,
          variables: {
            where: {
              Void_not: true,
              Cancel_not: true,
              PickupDate_gte: moment(PickupDate)
                .startOf("day")
                .toISOString(),
              PickupDate_lte: moment(PickupDate)
                .endOf("day")
                .toISOString(),
              PickupLocationRecIdRef: parseInt(PickupLocationRecIdRef, 10)
            }
          },
          fetchPolicy: "network-only"
        });

        const { data: AllOrderData } = await client.query({
          query: ALL_ORDERS_QUERY,
          variables: {
            where: {
              Void_not: true,
              Cancel_not: true,
              PickupDate_gte: moment(PickupDate)
                .startOf("day")
                .toISOString(),
              PickupDate_lte: moment(PickupDate)
                .endOf("day")
                .toISOString()
            }
          },
          fetchPolicy: "network-only"
        });

        const { data: locationData } = await client.query({
          query: SINGLE_LOCATION_QUERY,
          variables: {
            where: {
              RecId: parseInt(PickupLocationRecIdRef)
            }
          }
        });
        let newOrders = orderData.Orders;
        if (
          locationData &&
          locationData.Location &&
          locationData.Location.TimeZone
        ) {
          newOrders = orderData.Orders.map(order => ({
            ...order,
            PickupDate:
              order.PickupDate &&
              calcTimeZone(
                order.PickupDate,
                locationData.Location.TimeZone.CodeId
              ),
            PickupDateTime:
              order.PickupDateTime &&
              calcTimeZone(
                order.PickupDateTime,
                locationData.Location.TimeZone.CodeId
              ),
            ScheduledDeliveryTime:
              order.ScheduledDeliveryTime &&
              calcTimeZone(
                order.ScheduledDeliveryTime,
                locationData.Location.TimeZone.CodeId
              )
          }));
        }
        setOrders(newOrders);
        setScales(locationData?.Location?.NumberOfLoadingBays || 0);
        const locations = AllOrderData?.Orders?.map(
          x => x?.PickupLocation.RecId
        ).filter(r => r);

        setPickupLocations(locations);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.log(err);
        toast.error("Error getting Orders.");
      }
    };

    if (PickupDate && PickupLocationRecIdRef) {
      fetchData();
    }
  }, [PickupDate, PickupLocationRecIdRef, client]);

  return {
    orders,
    scales,
    loading,
    setOrders,
    pickupLocations
  };
};

const TripsPage = () => {
  const SystemCodes = useSystemCodes({
    Category: "Order",
    CodeName: "OrderStatus"
  });
  const client = useApolloClient();
  const filterState = getFilterState("TRIPS_FILTERS", {
    date: moment().format("YYYY-MM-DD"),
    location: null,
    interval: 15
  });

  const [date, setDate] = useState(filterState.date);
  const [location, setLocation] = useState(filterState.location);
  const [interval, setTimeInterval] = useState(filterState.interval);
  const [activeTrip, setActiveTrip] = useState(undefined);
  const [statusCodes, setStatusCodes] = useState(null);
  const [extraTripKeys, setExtraTripKeys] = useState([]);
  const { data: params } = useCompanyParameters();

  useEffect(() => {
    if (params.DefPickupLocationRecIdRef && !filterState.location) {
      setLocation(params.DefPickupLocationRecIdRef);
    }
  }, [filterState.location, params.DefPickupLocationRecIdRef]);

  useEffect(() => {
    if (params.DefTripInterval) {
      setTimeInterval(params.DefTripInterval);
    }

    if (SystemCodes?.data?.length > 0 && !statusCodes) {
      setStatusCodes({
        Scheduled: SystemCodes.data.find(s => s.CodeId === "SCH"),
        Unscheduled: SystemCodes.data.find(s => s.CodeId === "U")
      });
    }
  }, [SystemCodes, params.DefTripInterval, statusCodes]);

  const [updateOrder] = useMutation(UPDATE_ORDER_MUTATION, {
    refetchQueries: [
      "ALL_ORDERS_QUERY, SINGLE_LOCATION_QUERY",
      {
        query: GLOBAL_ALL_ORDERS_QUERY,
        variables: {
          where: {
            Void_not: true,
            Cancel_not: true,
            TripKey_startsWith: moment(date).format("YYYY-MM-DD")
          }
        }
      }
    ]
  });

  const { orders, scales, setOrders, pickupLocations, loading } = useFetch(
    date,
    location
  );

  useEffect(() => {
    if (Array.isArray(orders)) {
      setExtraTripKeys([
        ...new Set(orders.map(x => x.TripKey).filter(Boolean))
      ]);
    }
  }, [orders]);

  const resourceMap = [
    ...Array.from({ length: scales }, (x, i) => ({
      resourceId: i + 1,
      resourceTitle: `Bay ${i + 1}`
    })),
    { resourceId: scales + 2, resourceTitle: "Unscheduled" }
  ];

  const handleChange = fn => e => {
    if (e.target.value && parseInt(e.target.value)) {
      fn(e.target.value);
    } else {
      toast.error("You have to select one Pickup Location to filter Orders");
    }
  };

  useEffect(() => {
    saveFilterState("TRIPS_FILTERS", {
      date,
      location: parseInt(location, 10),
      interval
    });
  }, [date, location, interval]);

  const handleClear = useCallback(
    async o => {
      try {
        console.log(o);
        // if order being cleared is Leg === 1, untrip all orders on this Trip
        // otherwise untrip this order and decrement other Legs in Trip by 1
        const ordersToUpdate =
          o.Leg === 1 || !o.Leg
            ? orders
                .filter(x => x.Trip === o.Trip)
                .map(x => ({
                  data: {
                    Trip: null,
                    Leg: null,
                    BayNumber: null,
                    TripKey: null,
                    ComplexTrip: false,
                    StatusRecIdRef: statusCodes?.Unscheduled?.RecId,
                    PickupDriverRecIdRef: null,
                    DeliveryDriverRecIdRef: null,
                    PickupTractorRecIdRef: null,
                    DeliveryTractorRecIdRef: null,
                    PickupTrailerRecIdRef: null,
                    DeliveryTrailerRecIdRef: null
                  },
                  where: { RecId: x.RecId }
                }))
            : orders
                .filter(x => x.Trip === o.Trip)
                .map(x => ({
                  data: {
                    Trip:
                      x.Trip && x.Leg && x.RecId === o.RecId ? null : x.Trip,
                    Leg:
                      x.Trip && x.Leg && x.RecId === o.RecId
                        ? null
                        : x.Leg - (x.Leg > o.Leg ? 1 : 0),
                    TripKey: x.RecId === o.RecId ? null : x.TripKey,
                    ComplexTrip: x.RecId === o.RecId ? false : x.ComplexTrip,
                    BayNumber: x.RecId === o.RecId ? null : x.BayNumber,
                    StatusRecIdRef:
                      x.RecId === o.RecId
                        ? statusCodes?.Unscheduled?.RecId
                        : x.StatusRecIdRef,
                    PickupDriverRecIdRef:
                      x.RecId === o.RecId ? null : x.PickupDriverRecIdRef,
                    DeliveryDriverRecIdRef:
                      x.RecId === o.RecId ? null : x.DeliveryDriverRecIdRef,
                    PickupTractorRecIdRef:
                      x.RecId === o.RecId ? null : x.PickupTractorRecIdRef,
                    DeliveryTractorRecIdRef:
                      x.RecId === o.RecId ? null : x.DeliveryTractorRecIdRef,
                    PickupTrailerRecIdRef:
                      x.RecId === o.RecId ? null : x.PickupTrailerRecIdRef,
                    DeliveryTrailerRecIdRef:
                      x.RecId === o.RecId ? null : x.DeliveryTrailerRecIdRef
                  },
                  where: {
                    RecId: x.RecId
                  }
                }));

        const { data: OtherOrdersData } = await client.query({
          query: ALL_ORDERS_QUERY,
          variables: {
            where: {
              TripKey: o.TripKey,
              OR: [
                {
                  PickupLocationRecIdRef_not: o.PickupLocationRecIdRef
                },
                {
                  PickupDate_lte: moment(date)
                    .startOf("day")
                    .toISOString()
                },
                {
                  PickupDate_gte: moment(date)
                    .endOf("day")
                    .toISOString()
                }
              ]
            }
          },
          fetchPolicy: "network-only"
        });

        const otherOrders = OtherOrdersData?.Orders || [];

        const otherOrdersToUpdate =
          o.Leg === 1 || !o.Leg
            ? otherOrders
                .filter(x => x.Trip === o.Trip)
                .map(x => ({
                  data: {
                    Trip: null,
                    Leg: null,
                    BayNumber: null,
                    TripKey: null,
                    ComplexTrip: false,
                    StatusRecIdRef: statusCodes?.Unscheduled?.RecId,
                    PickupDriverRecIdRef: null,
                    DeliveryDriverRecIdRef: null,
                    PickupTractorRecIdRef: null,
                    DeliveryTractorRecIdRef: null,
                    PickupTrailerRecIdRef: null,
                    DeliveryTrailerRecIdRef: null
                  },
                  where: { RecId: x.RecId }
                }))
            : otherOrders
                .filter(x => x.Trip === o.Trip)
                .map(x => ({
                  data: {
                    Trip:
                      x.Trip && x.Leg && x.RecId === o.RecId ? null : x.Trip,
                    Leg:
                      x.Trip && x.Leg && x.RecId === o.RecId
                        ? null
                        : x.Leg - (x.Leg > o.Leg ? 1 : 0),
                    TripKey: x.RecId === o.RecId ? null : x.TripKey,
                    ComplexTrip: x.RecId === o.RecId ? false : x.ComplexTrip,
                    BayNumber: x.RecId === o.RecId ? null : x.BayNumber,
                    StatusRecIdRef:
                      x.RecId === o.RecId
                        ? statusCodes?.Unscheduled?.RecId
                        : x.StatusRecIdRef,
                    PickupDriverRecIdRef:
                      x.RecId === o.RecId ? null : x.PickupDriverRecIdRef,
                    DeliveryDriverRecIdRef:
                      x.RecId === o.RecId ? null : x.DeliveryDriverRecIdRef,
                    PickupTractorRecIdRef:
                      x.RecId === o.RecId ? null : x.PickupTractorRecIdRef,
                    DeliveryTractorRecIdRef:
                      x.RecId === o.RecId ? null : x.DeliveryTractorRecIdRef,
                    PickupTrailerRecIdRef:
                      x.RecId === o.RecId ? null : x.PickupTrailerRecIdRef,
                    DeliveryTrailerRecIdRef:
                      x.RecId === o.RecId ? null : x.DeliveryTrailerRecIdRef
                  },
                  where: {
                    RecId: x.RecId
                  }
                }));

        await Promise.all(
          otherOrdersToUpdate.map(variables => updateOrder({ variables }))
        );

        const result = await Promise.all(
          ordersToUpdate.map(variables => updateOrder({ variables }))
        );

        const nextOrders = [...orders];
        result.forEach(({ data }) => {
          const idx = nextOrders.findIndex(
            y => data.updateOrder.RecId === y.RecId
          );
          nextOrders.splice(idx, 1, data.updateOrder);
        });
        setOrders(nextOrders);
      } catch (err) {
        toast.error("Error updating Orders.");
      }
    },
    [client, date, orders, setOrders, statusCodes, updateOrder]
  );
  const handleUpdateOrder = useCallback(
    async (order, isMove = false) => {
      try {
        if (!isMove && !order.Trip && !order.Leg) {
          return handleClear(order);
        }
        if (
          order.Trip &&
          order.Leg &&
          orders.some(
            x =>
              x.RecId !== order.RecId &&
              x.Trip === order.Trip &&
              x.Leg === order.Leg
          )
        ) {
          throw new Error("Another order exists for this Trip and Leg");
        }
        const { data: locationData } = await client.query({
          query: SINGLE_LOCATION_QUERY,
          variables: {
            where: {
              RecId: parseInt(location)
            }
          },
          fetchPolicy: "network-only"
        });
        if (
          locationData &&
          locationData.Location &&
          locationData.Location.TimeZone
        ) {
          const { PickupDate, ScheduledDeliveryDate } = calculateOrderDates({
            ...order,
            PickupDate: new Date(order.PickupDate)
          });

          const variables = {
            data: {
              PickupDate,
              ScheduledDeliveryDate,
              Trip: order.Trip,
              Leg: order.Leg,
              BayNumber: order.BayNumber,
              TripKey: order.TripKey,
              StatusRecIdRef:
                order.Trip && order.Leg
                  ? statusCodes.Scheduled.RecId
                  : statusCodes.Unscheduled.RecId
            },
            where: {
              RecId: order.RecId
            }
          };
          const { data: TrippedOrders } = await client.query({
            query: ALL_ORDERS_QUERY,
            variables: {
              where: {
                Trip: order.Trip,
                PickupDate_gte: moment(date)
                  .startOf("day")
                  .toISOString(),
                PickupDate_lte: moment(date)
                  .endOf("day")
                  .toISOString(),
                TripKey_not: order.TripKey,
                RecId_not: order.RecId
              },
              orderByMulti: ["Leg_DESC"],
              first: 1
            },
            fetchPolicy: "network-only"
          });

          const lastLeg = TrippedOrders?.Orders?.[0];

          if (lastLeg) {
            variables.data.Leg = null;
            variables.data.TripKey = lastLeg.TripKey;
          }

          const { data } = await updateOrder({ variables });
          const idx = orders.findIndex(o => o.RecId === data.updateOrder.RecId);
          const nextOrders = [...orders];
          nextOrders.splice(idx, 1, {
            ...data.updateOrder,
            PickupDate: calcTimeZone(
              data.updateOrder.PickupDate,
              locationData.Location.TimeZone.CodeId
            ),
            PickupDateTime:
              data.updateOrder.PickupDateTime &&
              calcTimeZone(
                data.updateOrder.PickupDateTime,
                locationData.Location.TimeZone.CodeId
              ),
            ScheduledDeliveryTime:
              data.updateOrder.ScheduledDeliveryTime &&
              calcTimeZone(
                data.updateOrder.ScheduledDeliveryTime,
                locationData.Location.TimeZone.CodeId
              )
          });
          setOrders(nextOrders);
          // toast.success(`Order #${order.OrderNumber} updated successfully`);
        }
      } catch (err) {
        toast.error("Error updating Order.");
      }
    },
    [
      client,
      date,
      handleClear,
      location,
      orders,
      setOrders,
      statusCodes,
      updateOrder
    ]
  );

  const handleMoveEvent = async ({ event, start, resourceId }) => {
    const { order } = event;
    const idx = orders.indexOf(order);

    if (!order.Trip) {
      const getNextTrip = async () => {
        try {
          const { data: OrdersToday } = await client.query({
            query: ALL_ORDERS_QUERY,
            variables: {
              where: {
                PickupDate_gte: moment(filterState.date)
                  .startOf("day")
                  .toISOString(),
                PickupDate_lte: moment(filterState.date)
                  .endOf("day")
                  .toISOString()
              }
            },
            fetchPolicy: "network-only"
          });
          const trips = [...new Set(OrdersToday.Orders.map(x => x.Trip))];
          return Math.max(...trips) + 1;
        } catch (err) {
          toast.error("Error moving Event.");
        }
      };
      const nextTrip = await getNextTrip();
      order.Trip = nextTrip;
    }

    const isOverlapping = orders
      .filter(someOrder => someOrder.RecId !== order.RecId)
      .some(someOrder => {
        const isSame = moment(start).isSame(someOrder.PickupDate);
        const isSameBay =
          resourceId < scales + 1 && someOrder.BayNumber === resourceId;
        const isBetween = moment(start).isBetween(
          moment(someOrder.PickupDate),
          moment(someOrder.PickupDate).add(someOrder.EstimatedLoad, "hours")
        );

        return isSameBay && (isSame || isBetween);
      });

    if (!isOverlapping) {
      const pickupDate = moment(order.PickupDate).format("YYYY-MM-DD");

      const TripKey = order.TripKey
        ? `${order.TripKey.slice(0, order.TripKey.lastIndexOf("_"))}_${
            order.Trip
          }`
        : `${pickupDate}_${order.PickupLocationRecIdRef}_${order.Trip}`;

      const updatedOrder = {
        ...order,
        Trip: resourceId > scales + 1 ? null : order.Trip,
        Leg: resourceId > scales + 1 ? null : order.Leg,
        PickupDate: new Date(start),
        BayNumber: resourceId > scales + 1 ? null : resourceId,
        TripKey: resourceId > scales + 1 ? null : TripKey
      };

      updatedOrder.StatusRecIdRef =
        updatedOrder.Trip && updatedOrder.Leg && updatedOrder.BayNumber
          ? statusCodes.Scheduled.RecId
          : statusCodes.Unscheduled.RecId;

      const nextOrders = [...orders];
      nextOrders.splice(idx, 1, updatedOrder);
      setOrders(nextOrders);
      handleUpdateOrder(updatedOrder, true);
    }
  };

  const eventList = orders.map(order => ({
    id: order.RecId,
    title: (
      <OrderItem
        active={order.Trip === activeTrip}
        key={order.RecId}
        order={order}
        untripped={!order.Trip}
        updateOrder={handleUpdateOrder}
        onClear={handleClear}
      />
    ),
    start: moment(order.PickupDate).toDate(),
    end: new Date(
      Math.min(
        moment(order.PickupDate)
          .add(order.EstimatedLoad, "hours")
          .toDate(),
        moment(order.PickupDate)
          .endOf("day")
          .toDate()
      )
    ),
    order,
    resourceId: order.BayNumber || scales + 2
  }));

  const handleSetActiveTrip = activeIndex => {
    setActiveTrip(activeIndex + 1);
  };

  return (
    <div className="trip-page-container">
      <div className="trip-page-head">
        <div className="trip-page-head-start">
          <Column.Group>
            <Column size={3}>
              <Field>
                <Control>
                  <Label>Pickup Date</Label>
                  <DateInput
                    name="TripDate"
                    value={date}
                    onChange={handleChange(setDate)}
                  />
                </Control>
              </Field>
            </Column>
            <Column size={3}>
              <Field>
                <Control>
                  <Label>Pickup Location</Label>
                  {pickupLocations?.length > 0 ? (
                    <LocationSelect
                      name="TripLocation"
                      showHelp={false}
                      value={location}
                      where={{
                        RecId_in: pickupLocations
                      }}
                      onChange={handleChange(setLocation)}
                    />
                  ) : (
                    <Input
                      disabled
                      readOnly
                      value="No orders found for this day."
                    />
                  )}
                </Control>
              </Field>
            </Column>
            <Column size={2}>
              <Field>
                <Control>
                  <Label>Interval</Label>
                  <Select.Container fullwidth>
                    <Select
                      value={interval}
                      onChange={handleChange(x => setTimeInterval(parseInt(x)))}
                    >
                      <Select.Option value="15">15 minutes</Select.Option>
                      <Select.Option value="30">30 minutes</Select.Option>
                    </Select>
                  </Select.Container>
                </Control>
              </Field>
            </Column>
            <Column size={2}>
              <Field>
                <Control>
                  <Label>&nbsp;</Label>
                  <Button color="primary">Refresh</Button>
                </Control>
              </Field>
            </Column>
          </Column.Group>
        </div>
        <div className="trip-page-head-end">
          {/* <Button onClick={handleReset}>REset</Button> */}
        </div>
      </div>
      <TripList
        setActiveTrip={handleSetActiveTrip}
        tripKeys={[date, ...extraTripKeys]}
      />
      {loading && <Loading />}
      {orders && orders.length && !loading ? (
        <div>
          <div className="trip-page-body">
            <ResourceScheduler
              defaultDate={date}
              events={eventList}
              resources={resourceMap}
              step={interval}
              onMoveEvent={handleMoveEvent}
            />
          </div>
        </div>
      ) : null}

      {/* <pre>
        <code>{JSON.stringify(orders, null, 2)}</code>
      </pre> */}
    </div>
  );
};

export default TripsPage;
